import React, { useState } from 'react';
import {
  ShareFat,
  DownloadSimple,
  ArrowsOut,
  ArrowsIn,
} from '@phosphor-icons/react';
import { Link, useParams } from 'react-router-dom';

import {
  trackShareOpenShareHubModal,
  trackShareDownload,
  trackShareToggleVideoSize,
  trackShareReOpenWorkflow,
  trackSampleStartAgain,
} from '../../../../analytics';

import { LinkBox } from '../../../../components/custom';
import { Button, Modal } from '../../../../components/general';
import { PageHeader } from '../../../../components/layout/content';
import { zipAndDownloadVideos } from '../../../../components/video';

import { URL } from '../../../../constants';

import useSampleProjectData from '../../../../hooks/useSampleProjectData';

import { useProjectStore } from '../../../../stores/project';

import { Person } from '../../../../types';
import {
  isEmpty,
  isPlural,
  getPluralPhrase,
  getUrl,
  getShareLink,
} from '../../../../utils';

interface Props {
  people: Person[];
  isIncreased: boolean;
  setIsIncreased: React.Dispatch<React.SetStateAction<boolean>>;
}

const SharePageHeader: React.FC<Props> = (props) => {
  const { eventId = '' } = useParams();

  const { currentProject } = useProjectStore();

  const { people, isIncreased, setIsIncreased } = props;

  const [isDownloading, setIsDownloading] = useState(false);
  const [showShareHubModal, setShowShareHubModal] = useState(false);

  // sample project data
  const { isSample, sampleProjectData, resetSampleProjectData } =
    useSampleProjectData(eventId);
  const hubId = isSample ? sampleProjectData.event.eventId : eventId;

  if (!currentProject) return null;

  const resetSample = () => {
    resetSampleProjectData();
    trackSampleStartAgain('Share');
  };

  const handleDownloadAll = async () => {
    setIsDownloading(true);

    const videoUrls: { url: string; faceName: string }[] = [];

    people.forEach((person) =>
      videoUrls.push({
        url: person.videoUrl ?? '',
        faceName: person.face.name,
      }),
    );

    await zipAndDownloadVideos(currentProject.name, videoUrls);

    trackShareDownload('hub', isSample);

    setIsDownloading(false);
  };

  const iconBtns = [
    {
      Icon: ShareFat,
      onClick: () => {
        setShowShareHubModal(true);
        trackShareOpenShareHubModal(isSample);
      },
      popoverData: {
        header: 'Share Hub Page',
        body: 'Hub Page has all personalised highlight reels in a single view',
      },
    },
    {
      Icon: DownloadSimple,
      onClick: () => handleDownloadAll(),
      isProcessing: isDownloading,
      popoverData: {
        header: 'Download all',
        body: 'All generated videos in one ZIP file',
      },
    },
    {
      Icon: isIncreased ? ArrowsIn : ArrowsOut,
      onClick: () => {
        setIsIncreased(!isIncreased);
        trackShareToggleVideoSize(!isIncreased, isSample);
      },
      popoverData: {
        header: isIncreased ? 'Reduce' : 'Increase',
        body: isIncreased
          ? 'Reduce the video size for an easy overview of all videos at a glance'
          : 'Increase the video size for an easy review of each generated video',
      },
      show: window.innerWidth >= 640,
    },
  ];

  return (
    <React.Fragment>
      <PageHeader
        heading={`Share – ${currentProject.name}`}
        subheading={
          <>
            Personalised Highlight Reel{isPlural(people)}{' '}
            {isPlural(people) ? 'are' : 'is'} ready to share with{' '}
            {getPluralPhrase(people, 'person')}!{' '}
            {isSample ? (
              <span>
                Want to try the sample project again?{' '}
                <Link
                  to={getUrl(URL.VIDEO_BUILDER, eventId)}
                  onClick={resetSample}
                >
                  Re-start the workflow
                </Link>
              </span>
            ) : (
              <span>
                Want to generate reels for more people?{' '}
                <Link
                  to={getUrl(URL.VIDEO_BUILDER, eventId)}
                  onClick={trackShareReOpenWorkflow}
                >
                  Re-open the workflow
                </Link>
              </span>
            )}
          </>
        }
        icons={
          !isEmpty(people) && (
            <div className="flex">
              {iconBtns.map(
                (btn) =>
                  btn.show !== false && (
                    <Button
                      key={btn.popoverData.header}
                      onClick={btn.onClick}
                      variant="square"
                      colour="transparent-black"
                      isSubmitBtn
                      isProcessing={btn.isProcessing}
                      spinner={{
                        size: 'sm',
                        color: 'black',
                        hoverColor: 'white',
                      }}
                      popover={btn.popoverData}
                    >
                      <btn.Icon size="1.25rem" />
                    </Button>
                  ),
              )}
            </div>
          )
        }
      />

      {/* share hub modal */}
      <Modal
        show={showShareHubModal}
        onHide={() => setShowShareHubModal(false)}
      >
        <Modal.Body centered>
          <h4>Your Project Share Hub</h4>

          {isSample ? (
            <div>
              This is an <b>example page</b> and it doesn't reflect the choices
              you've made before.
            </div>
          ) : (
            <div>
              This is a shareable page for this project, all personalised
              highlight reels are visible here.
            </div>
          )}

          <LinkBox type="hub" link={getShareLink(hubId)} />

          <Button className="w-1/2" onClick={() => setShowShareHubModal(false)}>
            OK
          </Button>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default SharePageHeader;
